// Companion/blocks/setResultText.ts
//
// The one-line summary of a finished or paused study set: "Finished Renal &
// Urinary System · 15 of 15 cards · 71% correct".
//
// Two surfaces say it, so it is written once here (ORA-833): the server-written
// `set_result` row the thread gets when a student comes back from a full session
// (`SetResultBlock`), and a set the student folded away in the thread after
// finishing it there (`StudySetBlock`). A set should read the same either way.

import type {
  ChatSetResultBlockPayload,
  ChatStudySetKind,
} from '@/types/chatBlocks';

/**
 * The fields the line is written from. A `set_result` payload is exactly this;
 * a study-set block is this plus its items, and passes its *live* progress
 * rather than the served one.
 */
export type SetResultSummary = Pick<
  ChatSetResultBlockPayload,
  'kind' | 'status' | 'progress' | 'scopeLabel'
>;

/** What one item of a set is called, matching the server's scope labels. */
const NOUNS: Record<ChatStudySetKind, { one: string; many: string }> = {
  question: { one: 'question', many: 'questions' },
  flashcard: { one: 'card', many: 'cards' },
  video: { one: 'video', many: 'videos' },
};

const setItemNoun = (kind: ChatStudySetKind, count: number): string =>
  count === 1 ? NOUNS[kind].one : NOUNS[kind].many;

/** What a set's items are called: "questions", "cards", "videos". */
export const setItemsNoun = (kind: ChatStudySetKind): string =>
  NOUNS[kind].many;

/** True once the set has nothing left in it. */
export const isSetComplete = (summary: SetResultSummary): boolean =>
  summary.status === 'completed' ||
  (summary.progress.total > 0 &&
    summary.progress.done >= summary.progress.total);

/**
 * The scope label without the count the server put in it.
 *
 * A served set is titled "Renal & Urinary System · 15 cards" or "3 Renal
 * questions", which is right on the block itself; on this line the count comes
 * back as "15 of 15 cards", and the label saying it too read as a stutter
 * (Kevin, 2026-09-17). A trailing count of THIS set's kind goes whole; a
 * leading one loses only its number, because the words after it are what the
 * set is — "2 due Renal cards" is still "due Renal cards".
 */
const scopeWithoutCount = (
  scopeLabel: string,
  kind: ChatStudySetKind
): string => {
  const { one, many } = NOUNS[kind];
  const noun = `(?:${many}|${one})`;

  return scopeLabel
    .replace(new RegExp(`(?:^|\\s·\\s|\\s)\\d+\\s${noun}$`), '')
    .replace(new RegExp(`^\\d+\\s+(?=.*\\b${noun}$)`), '')
    .trim();
};

/** "71% correct", when the set counted answers and there were any. */
const accuracyText = ({ progress }: SetResultSummary): string | null => {
  if (progress.correct === undefined || progress.done === 0) return null;

  return `${Math.round((progress.correct / progress.done) * 100)}% correct`;
};

/**
 * The whole line: what happened, to which set, and how far it got — each of
 * those said once. "Finished Renal & Urinary System · 15 of 15 cards".
 */
export const setResultText = (summary: SetResultSummary): string => {
  const isComplete = isSetComplete(summary);
  const { done, total } = summary.progress;
  const count = `${done} of ${total} ${setItemNoun(summary.kind, total)}`;

  const parts = isComplete
    ? [scopeWithoutCount(summary.scopeLabel, summary.kind), count]
    : [`at ${count}`];

  const line = [...parts, accuracyText(summary)]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

  return `${isComplete ? 'Finished' : 'Paused'} ${line}`;
};
